/**
 * Settings → Skills, in the shape of the Models tab: one row a skill, with
 * Edit, a SKILL.md export and the switch that turns it on or off. The editor
 * is a dialog holding the name, description, instructions and automatic use,
 * and Delete. Below them, the built-in commands, to read only.
 *
 * Import reads a SKILL.md in the browser (skillFile.ts) and opens it in the
 * editor as a new skill, automatic use off, so nothing is added before the
 * person has read it. The list is app state, loaded once (state.tsx), so the
 * composer's slash menu has every change made here.
 */

import { useRef, useState } from 'react';
import { BUILTIN_SKILLS } from '@wire';
import { ApiError, createSkill, deleteSkill, patchSkill, type Skill } from '../../api';
import { ds } from '../../ds';
import { readSkillFile, skillFile, type SkillFields } from '../../skillFile';
import { useApp } from '../../state';
import { GROUP_ICON } from '../composer/slash';
import { IconTap, MonoLabel, Ph } from '../ui';

type Editing = { skill: Skill } | { draft: SkillFields; imported: boolean };

const FAILED = 'That did not go through. Try again.';

function download(skill: Skill) {
  const url = URL.createObjectURL(new Blob([skillFile(skill)], { type: 'text/markdown' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'SKILL.md';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const iconTile = {
  width: 32,
  height: 32,
  flex: 'none',
  borderRadius: 9,
  background: 'var(--app-panel-sunken)',
  color: 'var(--text-muted)',
  display: 'grid',
  placeItems: 'center'
} as const;

/** A built-in command: its name and what it does, and nothing to change. */
function BuiltinRow({ name, icon, description }: { name: string; icon: string; description: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 58, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
      <span style={iconTile}>
        <Ph name={icon} size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>/{name}</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {description}
        </span>
      </div>
    </div>
  );
}

function SkillRow({
  skill,
  busy,
  onEdit,
  onToggle
}: {
  skill: Skill;
  busy: boolean;
  onEdit: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const { Switch } = ds();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 58, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
      <span style={iconTile}>
        <Ph name="lightning" size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            fontWeight: 600,
            color: skill.enabled ? 'var(--text-strong)' : 'var(--text-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          /{skill.name}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {skill.description}
        </span>
      </div>
      <button type="button" className="bare hov-strong" onClick={onEdit} style={{ fontSize: 13, color: 'var(--text-link)' }}>
        Edit
      </button>
      <IconTap icon="download-simple" label={`Export /${skill.name}`} onClick={() => download(skill)} />
      <Switch checked={skill.enabled} disabled={busy} onChange={onToggle} />
    </div>
  );
}

function SkillDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const { Dialog, Button, Input, Switch } = ds();
  const { upsertSkill, removeSkill } = useApp();
  const skill = 'skill' in editing ? editing.skill : null;
  const start = 'skill' in editing ? editing.skill : editing.draft;
  const [name, setName] = useState(start.name);
  const [description, setDescription] = useState(start.description);
  const [instructions, setInstructions] = useState(start.instructions);
  const [auto, setAuto] = useState(start.auto);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<{ field: string | null; message: string } | null>(null);

  const fail = (err: unknown) => {
    const field = err instanceof ApiError && typeof err.details.field === 'string' ? err.details.field : null;
    setError({ field, message: err instanceof ApiError ? err.message : FAILED });
    setConfirming(false);
    setBusy(false);
  };

  const save = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const fields = { name: name.trim(), description: description.trim(), instructions: instructions.trim(), auto };
    (skill ? patchSkill(skill.id, fields) : createSkill(fields))
      .then((saved) => {
        upsertSkill(saved);
        onClose();
      })
      .catch(fail);
  };

  const remove = () => {
    if (!skill || busy) return;
    setBusy(true);
    deleteSkill(skill.id)
      .then(() => {
        removeSkill(skill.id);
        onClose();
      })
      .catch(fail);
  };

  const fieldError = (field: string) => (error?.field === field ? error.message : undefined);
  const ready = !!name.trim() && !!description.trim() && !!instructions.trim();

  return (
    <Dialog
      open
      position="fixed"
      width={560}
      title={skill ? `/${skill.name}` : 'imported' in editing && editing.imported ? 'Import skill' : 'New skill'}
      onClose={onClose}
      style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16, maxHeight: '100%', overflowY: 'auto' }}
      footer={
        confirming && skill ? (
          <>
            <span style={{ flex: 1, alignSelf: 'center', fontSize: 13.5, color: 'var(--text-body)' }}>Delete /{skill.name}?</span>
            <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" density="app" size="sm" disabled={busy} onClick={remove}>
              Delete
            </Button>
          </>
        ) : (
          <>
            {skill ? (
              <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
                Delete
              </Button>
            ) : null}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" density="app" size="sm" disabled={busy || !ready} onClick={save}>
              {skill ? 'Save' : 'Add'}
            </Button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s/g, '-'))}
          hint={`Type /${name.trim() || 'name'} in a message to run it.`}
          error={fieldError('name')}
          autoFocus={!skill}
        />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} error={fieldError('description')} />
        <Input
          label="Instructions"
          multiline
          rows={10}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          error={fieldError('instructions')}
        />
        <Switch label="Assist can use it when it fits" checked={auto} onChange={setAuto} />
        {error && !error.field ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error.message}</span> : null}
      </div>
    </Dialog>
  );
}

export function SkillsTab() {
  const { skills, upsertSkill } = useApp();
  const { Button } = ds();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  const toggle = (skill: Skill, enabled: boolean) => {
    if (busyId) return;
    setBusyId(skill.id);
    patchSkill(skill.id, { enabled })
      .then(upsertSkill)
      .catch(() => {})
      .finally(() => setBusyId(null));
  };

  const importFile = (file: File | undefined) => {
    if (!file) return;
    setImportError(null);
    file
      .text()
      .then((text) => setEditing({ draft: { ...readSkillFile(text), auto: false }, imported: true }))
      .catch(() => setImportError('That file could not be read.'));
  };

  if (!skills) return null;
  const sorted = [...skills].sort((a, b) => a.name.localeCompare(b.name));
  const ownNames = new Set(skills.filter((s) => s.enabled).map((s) => s.name));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {sorted.length ? (
        <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
          {sorted.map((s) => (
            <SkillRow key={s.id} skill={s} busy={busyId === s.id} onEdit={() => setEditing({ skill: s })} onToggle={(v) => toggle(s, v)} />
          ))}
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>No skills yet.</p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <Button
          variant="primary"
          density="app"
          size="sm"
          iconLeft="plus"
          onClick={() => setEditing({ draft: { name: '', description: '', instructions: '', auto: true }, imported: false })}
        >
          New skill
        </Button>
        <Button variant="ghost" density="app" size="sm" iconLeft="upload-simple" onClick={() => picker.current?.click()}>
          Import SKILL.md
        </Button>
        <input
          ref={picker}
          type="file"
          accept=".md,text/markdown"
          hidden
          onChange={(e) => {
            importFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      {importError ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{importError}</span> : null}

      <MonoLabel style={{ paddingTop: 8 }}>Built-in commands</MonoLabel>
      <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
        {BUILTIN_SKILLS.map((b) => (
          <BuiltinRow
            key={b.name}
            name={b.name}
            icon={GROUP_ICON[b.group]}
            description={ownNames.has(b.name) ? 'Your skill with this name runs instead.' : b.description}
          />
        ))}
      </div>

      {editing ? <SkillDialog editing={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}
